import {
  faCheckCircle,
  faSpinner,
  faWarning
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { clsx } from "clsx";
import type { ReactNode } from "react";

type ScaleStatusViewProps = {
  tone: "loading" | "success" | "error";
  title: ReactNode;
  description: ReactNode;
  actions: ReactNode;
};

/** The centred icon, title, text and buttons shared by the waiting, done and error states. */
export const ScaleStatusView = ({
  tone,
  title,
  description,
  actions
}: ScaleStatusViewProps) => (
  <div className="flex w-full flex-col items-center gap-6 text-center">
    {tone === "loading" ? (
      <FontAwesomeIcon
        className="text-6xl text-text-secondary"
        icon={faSpinner}
        spinPulse
      />
    ) : (
      <FontAwesomeIcon
        className={clsx("text-6xl", {
          "text-primary-main": tone === "success",
          "text-danger-dark": tone === "error"
        })}
        icon={tone === "success" ? faCheckCircle : faWarning}
      />
    )}

    <div className="flex flex-col gap-2">
      <p className="text-2xl font-bold">{title}</p>
      <p className="text-base text-text-secondary">{description}</p>
    </div>

    <div className="flex w-full justify-center gap-4">{actions}</div>
  </div>
);

type ScaleStatusBadgeProps = {
  tone: "live" | "error";
  children: ReactNode;
};

export const ScaleStatusBadge = ({ tone, children }: ScaleStatusBadgeProps) => (
  <span
    className={clsx(
      "flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold",
      {
        "bg-success-backdrop text-success-dark": tone === "live",
        "bg-danger-backdrop text-danger-dark": tone === "error"
      }
    )}
  >
    <span
      className={clsx("size-2 rounded-full", {
        "bg-success-dark": tone === "live",
        "bg-danger-dark": tone === "error"
      })}
    />
    {children}
  </span>
);
